import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  linkedSignal,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { RouterLink } from '@angular/router';
import { ExpertUpdateType } from '@techspert-io/experts';
import { IOpportunity } from '@techspert-io/opportunities';
import { PatternsModule } from '@techspert-io/patterns';
import {
  AvailableSchedulePeriod,
  haveAvailableSchedulePeriodsChanged,
  mergeAvailableSchedulePeriods,
  ScheduleAddAvailableTimesFormComponent,
} from '@techspert-io/schedule-calendar';
import moment from 'moment-timezone';
import {
  catchError,
  EMPTY,
  filter,
  finalize,
  map,
  of,
  tap,
  throwError,
} from 'rxjs';
import {
  INTERVIEW_PERIOD_CONFLICT_STATUSES,
  InterviewPeriodService,
} from '../../../../../../features/interview-period/services/interview-period.service';
import { AlertComponent } from '../../../../../../shared/components/alert/alert.component';
import { IExpert } from '../../../../../../shared/models/expert.interface';
import { TimezonePreferenceService } from '../../../../../../shared/services/timezone-preference.service';
import { ExpertStoreService } from '../../../../../../shared/state/expert.service';
import { RequestAdditionalAvailabilityInterviewPeriodTableComponent } from './interview-period-table/request-additional-availability-interview-period-table.component';
import { calculateOffsetLabel } from './utils/calculate-offset';
import { filterBookablePeriods } from './utils/filter-bookable-periods';

export interface IRequestAdditionalAvailabilityDialogData {
  opportunity: IOpportunity;
  expert: IExpert;
}

@Component({
  selector: 'app-request-additional-availability',
  templateUrl: './request-additional-availability.component.html',
  styleUrls: ['./request-additional-availability.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AlertComponent,
    RequestAdditionalAvailabilityInterviewPeriodTableComponent,
    ScheduleAddAvailableTimesFormComponent,
    PatternsModule,
    MatDialogModule,
    MatIconModule,
    MatButtonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    RouterLink,
  ],
})
export class RequestAdditionalAvailabilityComponent {
  private readonly dialogRef = inject(
    MatDialogRef<RequestAdditionalAvailabilityComponent>
  );
  private readonly interviewPeriodService = inject(InterviewPeriodService);
  private readonly expertStoreService = inject(ExpertStoreService);
  private readonly destroyRef = inject(DestroyRef);

  /** The user's preferred timezone, which the added times start in */
  readonly timezoneName = inject(TimezonePreferenceService).selectedTimezone;

  readonly offset = computed(() => {
    const clientTimezone = this.timezoneName();
    const expertTimezone = this.data.expert.timezoneName;

    if (!expertTimezone) {
      return;
    }

    const now = new Date();

    return calculateOffsetLabel(
      moment.tz(now, clientTimezone),
      moment.tz(now, expertTimezone)
    );
  });

  readonly data =
    inject<IRequestAdditionalAvailabilityDialogData>(MAT_DIALOG_DATA);

  readonly expertName =
    this.data.expert.firstName ||
    this.data.expert.lastName ||
    'Your selected expert';

  readonly availableSchedulePeriods = signal<AvailableSchedulePeriod[]>([]);
  readonly additionalAvailableSchedulePeriods = signal<
    AvailableSchedulePeriod[]
  >([]);
  readonly interviewPeriodLastModified = signal<string>(null);
  readonly interviewPeriodLoading = signal(true);
  readonly interviewPeriodError = signal(false);

  readonly showInsufficientTimeAlert = signal(false);
  readonly showInterviewPeriodRefreshedAlert = signal(false);

  readonly isSubmitting = signal(false);
  readonly submitError = signal(false);

  readonly comments = new FormControl('', { nonNullable: true });

  readonly showAlert = linkedSignal(() => !!this.offset());
  readonly showAddTimes = signal(false);
  readonly showAddComments = signal(false);

  constructor() {
    this.loadInterviewPeriod();
  }

  loadInterviewPeriod() {
    this.interviewPeriodLoading.set(true);
    this.interviewPeriodError.set(false);

    this.fetchInterviewPeriod()
      .pipe(
        tap((response) => {
          if (!response) {
            this.interviewPeriodError.set(true);
          }
        }),
        finalize(() => this.interviewPeriodLoading.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  toggleShowAddTimes() {
    this.showAddTimes.update((showAddTimes) => !showAddTimes);
  }

  toggleShowAddComments() {
    this.showAddComments.update((showAddComments) => !showAddComments);
  }

  hideAlert() {
    this.showAlert.set(false);
  }

  addAvailableTimes(added: AvailableSchedulePeriod[]) {
    this.showInterviewPeriodRefreshedAlert.set(false);

    const now = Date.now();

    this.additionalAvailableSchedulePeriods.update((current) =>
      mergeAvailableSchedulePeriods(
        current,
        added.filter(({ end }) => Date.parse(end) > now)
      )
    );

    if (!this.showInsufficientTimeAlert()) {
      return;
    }

    const merged = mergeAvailableSchedulePeriods(
      this.availableSchedulePeriods(),
      this.additionalAvailableSchedulePeriods()
    );

    this.showInsufficientTimeAlert.set(!this.hasSufficientTime(merged));
  }

  removeAvailableTime(removed: AvailableSchedulePeriod) {
    this.additionalAvailableSchedulePeriods.update((current) =>
      mergeAvailableSchedulePeriods(current, [], [removed])
    );
  }

  submit() {
    if (
      this.interviewPeriodLoading() ||
      this.interviewPeriodError() ||
      this.isSubmitting()
    ) {
      return;
    }

    this.submitError.set(false);

    const merged = this.additionalAvailableSchedulePeriods().length
      ? mergeAvailableSchedulePeriods(
          this.availableSchedulePeriods(),
          this.additionalAvailableSchedulePeriods()
        )
      : this.availableSchedulePeriods();

    if (!this.hasSufficientTime(merged)) {
      this.showInsufficientTimeAlert.set(true);

      return;
    }

    this.isSubmitting.set(true);

    this.handleInterviewPeriodUpdate(merged)
      .pipe(
        filter((success) => !!success),
        tap(() => {
          this.expertStoreService.updateExpert({
            expertId: this.data.expert.expertId,
            type: ExpertUpdateType.RequestDifferentTime,
            payload: {
              clientTimezoneName: this.timezoneName(),
              notes: this.comments.value.trim(),
            },
          });
          this.dialogRef.close();
        }),
        catchError(() => {
          this.submitError.set(true);
          return EMPTY;
        }),
        finalize(() => this.isSubmitting.set(false))
      )
      .subscribe();
  }

  private hasSufficientTime(periods: AvailableSchedulePeriod[]) {
    return (
      filterBookablePeriods(periods, this.data.expert.callDuration).length > 0
    );
  }

  private handleInterviewPeriodUpdate(periods: AvailableSchedulePeriod[]) {
    if (
      !haveAvailableSchedulePeriodsChanged(
        this.availableSchedulePeriods(),
        periods
      )
    ) {
      return of(true);
    }

    return this.interviewPeriodService
      .updateAvailable(
        this.data.opportunity.opportunityId,
        periods,
        this.interviewPeriodLastModified()
      )
      .pipe(
        map((response) => {
          if (!response) {
            this.submitError.set(true);
            return false;
          }

          this.availableSchedulePeriods.set(response.body);
          this.additionalAvailableSchedulePeriods.set([]);
          this.interviewPeriodLastModified.set(response.lastModified);

          return true;
        }),
        catchError((error) => this.reloadOnConflict(error))
      );
  }

  private reloadOnConflict(error: unknown) {
    if (
      !(error instanceof HttpErrorResponse) ||
      !INTERVIEW_PERIOD_CONFLICT_STATUSES.includes(error.status)
    ) {
      return throwError(() => error);
    }

    return this.fetchInterviewPeriod().pipe(
      tap((reloaded) => {
        this.showInterviewPeriodRefreshedAlert.set(!!reloaded);
      }),
      map(() => false)
    );
  }

  private fetchInterviewPeriod() {
    return this.interviewPeriodService
      .getByOpportunityId(this.data.opportunity.opportunityId)
      .pipe(
        tap((response) => {
          if (!response) {
            return;
          }

          this.availableSchedulePeriods.set(response.body.available);
          this.interviewPeriodLastModified.set(response.lastModified);
        })
      );
  }
}
